import {
  Banner,
  BoundCheckboxField,
  BoundMultiSelectField,
  BoundTextField,
  Button,
  Css,
  FormLines,
  StaticField,
  SuperDrawerContent,
  SuperDrawerHeader,
  useModal,
  useSuperDrawer,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import {
  FeatureTypeDetailsFragment,
  FeatureTypesDocument,
  useDeleteFeatureTypeMutation,
  useFeatureTypes_RoomTypesQuery,
  useSaveFeatureTypeMutation,
} from "src/routes/admin/featureTypes/FeatureTypes.generated";
import { DeleteConfirmationModal } from "src/routes/components/DeleteConfirmationModal";

type FeatureTypeDrawerProps =
  /** Create a new feature type. If `parent` is provided, it's a child of that parent; otherwise it's a root feature. */
  | { mode: "create"; parent?: FeatureTypeDetailsFragment }
  /** Edit an existing feature type. */
  | { mode: "edit"; featureType: FeatureTypeDetailsFragment }
  /** View an existing feature type. */
  | { mode: "view"; featureType: FeatureTypeDetailsFragment; parent?: FeatureTypeDetailsFragment };

export function FeatureTypeDrawer(props: FeatureTypeDrawerProps) {
  const { mode } = props;
  const featureType = mode !== "create" ? props.featureType : undefined;
  const parent = mode !== "create" ? props.featureType.parent : props.parent;

  const roomTypesQuery = useFeatureTypes_RoomTypesQuery({ skip: !!parent });
  const [saveFeatureType, { loading }] = useSaveFeatureTypeMutation({
    refetchQueries: [FeatureTypesDocument],
    awaitRefetchQueries: true,
  });
  const [deleteFeatureType, { loading: deleting }] = useDeleteFeatureTypeMutation({
    refetchQueries: [FeatureTypesDocument],
    awaitRefetchQueries: true,
  });
  const { closeDrawer } = useSuperDrawer();
  const { openModal } = useModal();

  const formState = useFormState({
    config: formConfig,
    readOnly: mode === "view",
    init: {
      input: featureType,
      map: (ft) => ({
        id: ft.id,
        name: ft.name,
        allowedInBuilding: ft.allowedInBuilding,
        allowedInLevel: ft.allowedInLevel,
        roomTypeIds: ft.roomTypes.map((rt) => rt.id),
      }),
      ifUndefined: {
        id: undefined,
        name: "",
        allowedInBuilding: parent?.allowedInBuilding ?? false,
        allowedInLevel: parent?.allowedInLevel ?? false,
        roomTypeIds: parent?.roomTypes.map((rt) => rt.id) ?? [],
      },
    },
  });

  const handleDelete = useCallback(() => {
    if (!featureType) return;
    openModal({
      content: (
        <DeleteConfirmationModal
          entityType="Feature Type"
          confirmationMessage={
            <span>
              Are you sure you want to delete <strong>{featureType.name}</strong>? This will also delete any nested
              feature types. This action cannot be undone.
            </span>
          }
          onConfirmDelete={async () => {
            await deleteFeatureType({ variables: { input: { id: featureType.id } } });
            closeDrawer();
          }}
          disabled={deleting}
        />
      ),
    });
  }, [closeDrawer, deleteFeatureType, deleting, featureType, openModal]);

  const handleSave = useCallback(async () => {
    const value = formState.value;
    await saveFeatureType({
      variables: {
        input: {
          id: value.id,
          parentId: parent?.id,
          name: value.name,
          allowedInBuilding: value.allowedInBuilding,
          allowedInLevel: value.allowedInLevel,
          roomTypeIds: value.roomTypeIds ?? [],
        },
      },
    });
    closeDrawer();
  }, [closeDrawer, parent?.id, formState.value, saveFeatureType]);

  const title = useMemo(() => {
    if (mode === "create") return "Create Feature Type";
    if (mode === "edit") return `Edit ${featureType?.name}`;
    if (mode === "view") return `${featureType?.name}`;
    return "Feature Type";
  }, [mode, featureType]);

  return (
    <Observer>
      {() => (
        <>
          <SuperDrawerHeader title={title} hideControls />
          <SuperDrawerContent>
            {mode === "view" && (
              <div css={Css.mb3.$}>
                <Banner message="You do not have permission to feature types" type="info" />
              </div>
            )}
            <FormLines width="full">
              {parent && <StaticField label="Parent" value={parent.name} />}
              <BoundTextField label="Name" field={formState.name} />
              <BoundCheckboxField
                label="Allowed In Building"
                description="Can exist directly under Building"
                field={formState.allowedInBuilding}
                disabled={parent && !parent.allowedInBuilding && "Parent feature type is not allowed in Building"}
              />
              <BoundCheckboxField
                label="Allowed In Level"
                description="Can exist directly under a Level"
                field={formState.allowedInLevel}
                disabled={parent && !parent.allowedInLevel && "Parent feature type is not allowed in Level"}
              />
              <BoundMultiSelectField
                label="Room Types"
                field={formState.roomTypeIds}
                options={parent ? parent.roomTypes : (roomTypesQuery.data?.roomTypes ?? [])}
                getOptionValue={(rt) => rt.id}
                getOptionLabel={(rt) => rt.name}
                disabled={
                  parent &&
                  parent.roomTypes.isEmpty &&
                  "Parent feature type has no Room Types, so this feature cannot exist in any Room. Add Room Types to the parent first."
                }
              />
            </FormLines>
            {mode !== "view" && (
              <div css={Css.df.jcsb.aic.gap1.mt3.$}>
                <div>
                  {featureType && (
                    <Button
                      variant="tertiaryDanger"
                      icon="trash"
                      label="Delete"
                      onClick={handleDelete}
                      disabled={deleting}
                    />
                  )}
                </div>
                <div css={Css.df.gap1.$}>
                  <Button variant="tertiary" label="Cancel" onClick={closeDrawer} />
                  <Button
                    label={featureType ? "Save" : "Create"}
                    onClick={handleSave}
                    disabled={!formState.valid || loading}
                  />
                </div>
              </div>
            )}
          </SuperDrawerContent>
        </>
      )}
    </Observer>
  );
}

type FormValue = {
  id: string | null | undefined;
  name: string | null | undefined;
  allowedInBuilding: boolean | null | undefined;
  allowedInLevel: boolean | null | undefined;
  roomTypeIds: string[] | null | undefined;
};

const formConfig: ObjectConfig<FormValue> = {
  id: { type: "value" },
  name: { type: "value", rules: [required] },
  allowedInBuilding: { type: "value", rules: [required] },
  allowedInLevel: { type: "value", rules: [required] },
  roomTypeIds: { type: "value" },
};
